/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Button, Col, Container, Row, Table } from 'react-bootstrap'
import Breadcrumb from '../../components/Label/Breadcrumb'
import { Menu } from 'lucide-react'
import styles from './TakeDetail.module.css'
import api from '../../config/AxiosInstance'
import Loading from '../../components/atoms/Loading'
import toast, { Toaster } from 'react-hot-toast'
import { capitalizeFirstLetter } from '../../helpers/constants'

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#888',
  textTransform: 'uppercase',
  marginBottom: '0.2rem',
  letterSpacing: '0.5px'
}

const valueStyle = {
  fontSize: '1rem',
  fontWeight: '500',
  color: '#333'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

function TakeDetail() {
  const { state } = useLocation()
  const { id, slotId } = useParams()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  const renderAction = (dataValue) => (
    <Button
      variant="secondary"
      size="sm"
      key={dataValue.id}
      onClick={() => {
        setData((prevData) => ({
          ...prevData,
          items: prevData.items.map((item) => {
            if (item.id === dataValue.id) {
              return { ...item, isExpand: !item.isExpand }
            }
            return item
          })
        }))
      }}
    >
      <Menu size={14} />
    </Button>
  )

  const columns = [
    {
      key: 'item',
      label: 'Item Code - Name',
      colSpan: 5,
      component: (data) => `${data?.item_code || '-'} - ${data?.item || '-'}`
    },
    {
      key: 'action',
      label: 'Action',
      component: (data) => renderAction(data),
      className: 'text-end'
    }
  ]

  const columnSubs = [
    { key: 'batch', label: 'Batch' },
    { key: 'total_pack', label: 'Pack (sys)' },
    {
      key: 'total_pack_good_checker',
      label: 'Checker Good Qty',
      component: (data) => data?.total_pack_good_checker ?? '-'
    },
    {
      key: 'total_pack_damaged_checker',
      label: 'Checker Not Good Qty',
      component: (data) => data?.total_pack_damaged_checker ?? '-'
    },
    {
      key: 'total_pack_good_pic',
      label: 'PIC Good Qty',
      component: (data) => data?.total_pack_good_pic ?? '-'
    },
    {
      key: 'total_pack_damaged_pic',
      label: 'PIC Not Good Qty',
      component: (data) => data?.total_pack_damaged_pic ?? '-'
    }
  ]

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/stock-opname/${id}/${slotId}`)
      const { status, data: dataResponse } = res
      if (status === 200 && dataResponse.success) {
        setData({
          ...dataResponse.data,
          code: state?.code,
          items: dataResponse.data.items.map((item, index) => ({
            ...item,
            id: `${item.item_id}-${index}`,
            isExpand: true
          }))
        })
      } else {
        throw new Error(dataResponse.message)
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message ||
        err?.message ||
        'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id && slotId) {
      fetchData()
    } else {
      toast.error('Stock opname detail take not found!', {
        position: 'top-right',
        style: { fontSize: '14px', fontWeight: '600' }
      })
    }
  }, [id, slotId])

  const renderInfoItem = (label, value) => (
    <Col md={2}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value}</div>
    </Col>
  )

  if (loading) {
    return <Loading />
  }

  return (
    <Container fluid className="px-4">
      <Toaster />
      <Row className="mb-4">
        <Col md={12}>
          <Breadcrumb
            label={data?.code || ''}
            labelSub="OPNAME DETAIL"
            link="/stock-opname/stock-take"
            labelSubLink={`/stock-opname/stock-take/detail/${state?.id}`}
            back="STOCK TAKE"
            type="BadgeStatusStockTake"
            status={state?.status}
            stateValue={state}
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <div style={{ ...cardStyle }}>
            <Row>
              {renderInfoItem('Slot', data?.slot || '-')}
              {renderInfoItem('Rack Name', data?.rack || '-')}
              {renderInfoItem(
                'Rack Type',
                data?.rack_type ? capitalizeFirstLetter(data?.rack_type) : '-'
              )}
              {renderInfoItem('Checker', data?.checker || '-')}
              {renderInfoItem('PIC', data?.pic || '-')}
            </Row>
          </div>
        </Col>
      </Row>

      <div style={cardStyle}>
        <Row className="mb-3">
          <Col md={12}>
            <h6 className="text-uppercase text-muted opacity-75 mb-0">
              Item list
            </h6>
          </Col>
        </Row>
        <Col md={12}>
          <Table hover style={{ fontSize: '14px' }} className="mb-0">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    style={tableHeaderStyle}
                    className={`p-3 align-middle ${column.className || ''}`}
                    key={column.key}
                    colSpan={column.colSpan ?? 1}
                  >
                    {column.key !== 'checkbox' && <span>{column.label}</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.items?.filter((item) => item.item)?.length === 0 && (
                <tr key="data-not-found-1">
                  <td
                    colSpan={columnSubs.length}
                    className="text-center fw-bold p-4"
                  >
                    No Data
                  </td>
                </tr>
              )}
              {data?.items?.filter((item) => item.item).length > 0 &&
                data?.items
                  ?.filter((item) => item.item)
                  .map((item, i) => {
                    return (
                      <>
                        <tr key={`${item.id}-${i}`}>
                          {columns.map((column) => (
                            <td
                              key={column.key}
                              className={`p-3 align-middle ${
                                column.className || ''
                              }`}
                              style={{ borderBottom: '1px solid #f0f0f0' }}
                              colSpan={column.colSpan ?? 1}
                            >
                              {column.component && column.component(item)}
                              {column.key !== 'action' &&
                                !column.component &&
                                (item[column.key] ?? '-')}
                            </td>
                          ))}
                        </tr>
                        {item.isExpand && (
                          <tr>
                            <td colSpan={6} className="p-0 border-0">
                              <div
                                className={`p-3 ${
                                  styles['expandable-content']
                                } ${item.isExpand ? styles.expanded : ''}`}
                                style={{ backgroundColor: '#f8f9fa' }}
                              >
                                <Table
                                  size="sm"
                                  className="m-0 bg-white"
                                  bordered
                                >
                                  <thead>
                                    <tr className="bg-light">
                                      {columnSubs.map((column) => (
                                        <th
                                          className={`align-middle p-2 ${
                                            column.className || ''
                                          }`}
                                          key={column.key}
                                          style={{
                                            fontSize: '13px',
                                            color: '#666'
                                          }}
                                        >
                                          {column.key !== 'checkbox' && (
                                            <span>{column.label}</span>
                                          )}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item?.details?.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={columnSubs.length}
                                          className="text-center text-muted p-2"
                                        >
                                          No Data...
                                        </td>
                                      </tr>
                                    )}
                                    {item?.batches?.length > 0 &&
                                      item?.batches.map((itemDetail, i) => (
                                        <tr key={i}>
                                          {columnSubs.map((columnSub) => (
                                            <td
                                              key={columnSub.key}
                                              className={`p-2 align-middle ${
                                                columnSub.className || ''
                                              }`}
                                              style={{ fontSize: '13px' }}
                                            >
                                              {columnSub.component &&
                                                columnSub.component(itemDetail)}
                                              {columnSub.key !== 'action' &&
                                                !columnSub.component &&
                                                (itemDetail[columnSub.key] ??
                                                  '-')}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                  </tbody>
                                </Table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )
                  })}
            </tbody>
          </Table>
        </Col>
      </div>
    </Container>
  )
}

export default TakeDetail
